import { FC, ReactNode, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from 'src/context/auth'
import { useApplication } from 'src/context/application'
import { useRenewal } from 'src/context/renewal'
import {
  MainContainer,
  HeaderContainer,
  BodyContainer,
  FooterContainer,
  FooterColumn
} from './styles'
import { HiPhone, HiOutlinePhone, HiOutlineMail } from 'react-icons/hi'
import { TiMessages } from 'react-icons/ti'
import Stepper from '../../basic/Stepper'
import {
  newApplicationNavigationRoutes,
  newApplicationJudgeNavigationRoutes,
  renewalApplicationNavigationRoutes,
  renewalJudgeApplicationNavigationRoutes
} from 'src/constants/application'
import { tlieSources } from 'src/constants/tlie'

import logo1 from '../../../assets/texas-lawyers-insurance-logo.svg'
import accbusiness from '../../../assets/accreditedbusiness.svg'

interface Props {
  children?: ReactNode
}

const renewalsPathsNotToRenderStepper = [
  '/customer/renew/begin',
  '/customer/renew/type',
  '/customer/renew/pdf',
  '/customer/renew/verify'
]

const ApplicationMainLayout: FC<Props> = ({ children }) => {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { application } = useApplication()
  const { renewalData } = useRenewal()

  const currentStepper = useMemo(() => {
    if(!!user && user.role === 'Applicant' && pathname !== '/new') {
      if(application?.insuranceType === 'LAWYER') {
        return newApplicationNavigationRoutes
      } else {
        return newApplicationJudgeNavigationRoutes
      }
    }
    if(
      !!user && user.role === 'Client' &&
      pathname.includes('/customer/renew') &&
      !renewalsPathsNotToRenderStepper.includes(pathname)
    ) {
      if(renewalData?.insuranceType === 'LAWYER') {
        return renewalApplicationNavigationRoutes
      } else {
        return renewalJudgeApplicationNavigationRoutes
      }
    }

    return null
  }, [application?.insuranceType, pathname, renewalData?.insuranceType, user])

  return (
    <MainContainer>
      <HeaderContainer>
        <a href={tlieSources.mainSiteUrl}>
          <img src={logo1} alt="logo" width="132px" />
        </a>
        {currentStepper !== null ? <Stepper steps={currentStepper}/> : null}
        <div className='tlie-header-contact'>
          <span>Want to speak with customer service?</span>
          <a href={`tel:${tlieSources.phone}`}>
            <HiPhone fontSize="12.8px" /><strong>{tlieSources.formattedPhone}</strong>
          </a>
        </div>
      </HeaderContainer>
      <BodyContainer>
        <div>
          {children}
        </div>
      </BodyContainer>
      <FooterContainer>
        <div>
          <FooterColumn>
            <div>
              <strong>
                <a href={`tel:${tlieSources.phone}`}>
                  <HiOutlinePhone fontSize="18px" />
                  {tlieSources.formattedPhone}
                </a>
              </strong>
            </div>
            <div>
              <hr />
            </div>
            <div>
              Monday – Friday  |  9am – 5pm CT
            </div>
          </FooterColumn>
          <FooterColumn>
            <div>
              <strong>
                <TiMessages fontSize="18px" />
                Chat with us.
              </strong>
            </div>
            <div>
              <strong>
                <a href={`mailto:${tlieSources.infoEmail}`}>
                  <HiOutlineMail fontSize="18px" />
                  {tlieSources.infoEmail}
                </a>
              </strong>
            </div>
          </FooterColumn>
          <FooterColumn>
            <a href={tlieSources.accBusinessUrl} rel="noreferrer noopener" target="_blank" style={{ border: 'none' }}>
              <img src={accbusiness} alt="accredited business" />
            </a>
            <div>
              <a href={tlieSources.privacyPolicyUrl} rel="noreferrer noopener" target="_blank">Privacy Policy</a>
              <a href={tlieSources.subscribeUrl} rel="noreferrer noopener" target="_blank">Subscribe</a>
            </div>
          </FooterColumn>
        </div>
      </FooterContainer>
    </MainContainer>
  )
}

export default ApplicationMainLayout